import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, IcsCampaignLegalEntityColumns, IcsCampaignLegalEntityQueryComplexInput, IcsCampaignLegalEntitySimpleQueryInput, IcsCampaignLegalEntitySortInput, OperatorInput, OrderTypeInput, PagingInput } from "../../../graphql/generated";
import { map, Observable } from "rxjs";
import { IcsDashboardType } from "./campaign-dashboard.service";
import { Apollo } from "apollo-angular";
import { GET_ATTESTATION_MANAGE_SCOPE, GET_EVALUATION_MANAGE_SCOPE, UPDATE_ATTESTATION_SCOPE, UPDATE_EVALUATION_SCOPE } from "../../../graphql/gqls/dashboard-campaignGQL";

@Injectable({
    providedIn: 'root'
})
export class ManageCampaignService {
    private readonly apollo = inject(Apollo);

    getLegalEntitiesIcs(campaignId: string, parameters: any, dashboardType: IcsDashboardType): Observable<any> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);

        const query = dashboardType === 'ATTESTATION' ? GET_ATTESTATION_MANAGE_SCOPE : GET_EVALUATION_MANAGE_SCOPE;
        const dataField = dashboardType === 'ATTESTATION' ? 'getLegalEntitiesForIcsAttestationManageScope' : 'getLegalEntitiesForIcsEvaluationManageScope';

        return this.apollo.query<any>({
            query,
            variables: { campaignId, query: param.query, sort: param.sort, page: param.page },
            fetchPolicy: 'no-cache'
        }).pipe(map(result => result.data[dataField]));
    }

    updateScope(campaignId: string, legalEntityIds: string[], dashboardType: IcsDashboardType): Observable<any> {
        const mutation = dashboardType === 'ATTESTATION' ? UPDATE_ATTESTATION_SCOPE : UPDATE_EVALUATION_SCOPE;
        const dataField = dashboardType === 'ATTESTATION' ? 'updateIcsAttestationScope' : 'updateIcsEvaluationScope';

        return this.apollo.mutate<any>({
            mutation,
            variables: { campaignId, legalEntityIds }
        }).pipe(map(result => result.data?.[dataField]));
    }
}

export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: IcsCampaignLegalEntityQueryComplexInput, sort: IcsCampaignLegalEntitySortInput, page: PagingInput } {
    const query: IcsCampaignLegalEntitySimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    const mapped = ENUM_RISKMAP[field as keyof typeof ENUM_RISKMAP];
                    if (!mapped) {
                        console.warn(`[manage-campaign] filtro su colonna "${field}" ignorato: nessun mapping in ENUM_RISKMAP`);
                        return;
                    }
                    mapped.forEach(key => {

                        query.push({
                            field: key as IcsCampaignLegalEntityColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: key !== IcsCampaignLegalEntityColumns.LegalEntityCode ? values : values.map(v => v?.split(/\s-\s(.+)/)[0]?.trim())
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_RISKMAP?.[parameters?.order?.sortField as keyof typeof ENUM_RISKMAP]?.[0] as IcsCampaignLegalEntityColumns || IcsCampaignLegalEntityColumns.LegalEntityName,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 20
        }
    }
}

export const ENUM_RISKMAP = {
    auditControlApproach: [IcsCampaignLegalEntityColumns.AuditControlApproach],
    businessType: [IcsCampaignLegalEntityColumns.BusinessType],
    businessUnit: [IcsCampaignLegalEntityColumns.BusinessUnit],
    category: [IcsCampaignLegalEntityColumns.Category],
    chiefAuditor: [IcsCampaignLegalEntityColumns.ChiefAuditor],
    currentCampaign: [IcsCampaignLegalEntityColumns.CurrentCampaign],
    legalEntityName: [IcsCampaignLegalEntityColumns.LegalEntityCode],
    legalEntityCode: [IcsCampaignLegalEntityColumns.LegalEntityCode],
    legalEntitySize: [IcsCampaignLegalEntityColumns.LegalEntitySizeScore],
    organizationalUnit: [IcsCampaignLegalEntityColumns.OrganizationalUnit],
    macroBusinessUnit: [IcsCampaignLegalEntityColumns.MacroBusinessUnit],
    permissionLevel: [IcsCampaignLegalEntityColumns.PermissionLevel],
    startDate: [IcsCampaignLegalEntityColumns.StartDate],
    valueChain: [IcsCampaignLegalEntityColumns.ValueChain],
    status: [IcsCampaignLegalEntityColumns.Status],
    icsAuditControlApproach: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityAuditControlApproach],
    icsCategory: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityCategory],
    inScope: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityInScope],
    validationDate: [IcsCampaignLegalEntityColumns.CampaignLegalEntityValidationDate],
    icsBusinessUnit: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityBusinessUnit],
    icsOrganizationalUnit: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityOrganizationalUnit],
    icsEntitySize: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityEntitySize],
    icsTypeOfBusiness: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityTypeOfBusiness],
    icsValueChain: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityValueChain],
    icsStartDate: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityStartDate],
    icsLegalEntityStatus: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityStatus],
    icsLastRiskAssessmentValidationDate: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityLastRiskAssessmentValidationDate]
}
